import React, { useState, useEffect, useContext } from "react";
import {
  ArrowLeft,
  Loader,
  Save,
  AlertCircle,
  CheckCircle,
  Upload,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../../Context/AuthContextProvider";
import { put } from "../../../utils/api";

const EditSubmission = () => {
  const { loginUser } = useContext(AuthContext);
  const { submissionId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submission, setSubmission] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    price: "",
    category: "",
    description: "",
    premium: false,
    media: [],
  });

  useEffect(() => {
    if (loginUser && submissionId) {
      fetchSubmission();
    }
  }, [loginUser, submissionId]);

  const fetchSubmission = async () => {
    setLoading(true);
    try {
      const { get } = await import("../../../utils/api");
      const response = await get(`/domain/submission/${submissionId}`);

      if (response.success) {
        const sub = response.submission;
        setSubmission(sub);

        // Populate form
        setFormData({
          name: sub.name || "",
          price: sub.price || "",
          category: sub.category || "",
          description: sub.description || "",
          premium: sub.premium || false,
          media: sub.media || [],
        });
      }
    } catch (error) {
      toast.error("Failed to load submission details");
      navigate("/Dashboard/Mysubmissions");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.price || !formData.category) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.price <= 0) {
      toast.error("Price must be greater than 0");
      return;
    }

    setSaving(true);
    try {
      const response = await put(`/domain/editsubmission/${submissionId}`, {
        ...formData,
        userId: loginUser._id || loginUser.id,
      });

      if (response.success) {
        toast.success("Submission updated successfully!");
        navigate("/Dashboard/Mysubmissions");
      }
    } catch (error) {
      toast.error("Failed to update submission");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      media: prev.media.filter((_, i) => i !== index),
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-24 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-[#d4af37] animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading submission...</p>
        </div>
      </div>
    );
  }

  if (!submission) {
    return null;
  }

  if (submission.status !== "pending") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-24">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-red-300">
            <div className="text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Cannot Edit This Submission
              </h2>
              <p className="text-gray-600 mb-6">
                Only pending submissions can be edited. This submission is
                currently: <strong>{submission.status}</strong>
              </p>
              <button
                onClick={() => navigate("/Dashboard/Mysubmissions")}
                className="px-6 py-3 bg-gradient-to-r from-[#b99653] to-[#d4af37] text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                Back to My Submissions
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-24">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border-2 border-[#d4af37]">
          <button
            onClick={() => navigate("/Dashboard/Mysubmissions")}
            className="flex items-center gap-2 text-gray-600 hover:text-[#d4af37] mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to My Submissions
          </button>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-[#b99653] to-[#d4af37] bg-clip-text text-transparent">
            Edit Submission
          </h1>
          <p className="text-gray-600 mt-2">
            Update your domain details before admin approval
          </p>
        </div>

        {/* Info Alert */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg mb-6">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-blue-600" />
            <p className="text-sm text-blue-800">
              You can edit this submission because it's still pending admin
              review. Once approved, editing will be disabled.
            </p>
          </div>
        </div>

        {/* Edit Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Domain Name */}
          <div className="bg-white rounded-xl shadow-md p-6 border-2 border-gray-200">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Domain Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#d4af37] focus:outline-none transition-colors"
              placeholder="Enter domain name"
              required
            />
          </div>

          {/* Price */}
          <div className="bg-white rounded-xl shadow-md p-6 border-2 border-gray-200">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Price (₹) *
            </label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#d4af37] focus:outline-none transition-colors"
              placeholder="Enter price in rupees"
              min="0"
              required
            />
          </div>

          {/* Category */}
          <div className="bg-white rounded-xl shadow-md p-6 border-2 border-gray-200">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Category *
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#d4af37] focus:outline-none transition-colors"
              required
            >
              <option value="">Select a category</option>
              <option value="Business">Business</option>
              <option value="Technology">Technology</option>
              <option value="Finance">Finance</option>
              <option value="Healthcare">Healthcare</option>
              <option value="Education">Education</option>
              <option value="E-commerce">E-commerce</option>
              <option value="Entertainment">Entertainment</option>
              <option value="Travel">Travel</option>
              <option value="Food">Food</option>
              <option value="Real Estate">Real Estate</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Description */}
          <div className="bg-white rounded-xl shadow-md p-6 border-2 border-gray-200">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#d4af37] focus:outline-none transition-colors resize-none"
              placeholder="Describe your domain..."
            />
          </div>

          {/* Premium */}
          <div className="bg-white rounded-xl shadow-md p-6 border-2 border-gray-200">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="premium"
                checked={formData.premium}
                onChange={handleInputChange}
                className="w-5 h-5 text-[#d4af37] border-gray-300 rounded focus:ring-[#d4af37]"
              />
              <span className="text-sm font-semibold text-gray-700">
                Mark as Premium Domain
              </span>
            </label>
          </div>

          {/* Media Preview */}
          {formData.media && formData.media.length > 0 && (
            <div className="bg-white rounded-xl shadow-md p-6 border-2 border-gray-200">
              <label className="block text-sm font-semibold text-gray-700 mb-4">
                Current Images
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {formData.media.map((img, idx) => (
                  <div key={idx} className="relative group">
                    <img
                      src={img}
                      alt={`Media ${idx + 1}`}
                      className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(idx)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate("/Dashboard/Mysubmissions")}
              className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="white-btn !border-[#fae441] flex-1 px-6 py-3 text-black rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSubmission;